// init-badge.js
import Matter from 'matter-js';

const InitBadge = (
  inputLabels = [],
  {
    debug = false,
    startOnVisible = true,
    visibleThreshold = 0.35,
    visibleOnce = true,
    initialCount = null,
    randomInit = false,
  } = {}
) => {
  const container = document.querySelector('.box--matter');
  if (!container) return;

  // ===== 0) 라벨 정규화 =====
  const normalizeLabels = (arr) => {
    return arr.map((item, idx) => {
      if (typeof item === 'string') {
        return { id: `badge-${idx}`, label: item, color: '#fff' };
      }
      const id = item.id ?? `badge-${idx}`;
      const label =
        item.label ?? String(item.text ?? item.title ?? `Badge ${idx + 1}`);
      const color = item.color ?? '#fff';
      return { id, label, color, ...item };
    });
  };

  const labels = normalizeLabels(inputLabels);
  if (!labels.length)
    labels.push({ id: 'badge-0', label: 'Hello Badge', color: '#fff' });

  const {
    Engine,
    Runner,
    World,
    Bodies,
    Body,
    Sleeping,
    Composite,
    Mouse,
    MouseConstraint,
    Events,
    Render,
  } = Matter;

  // 컨테이너 안전 스타일
  container.style.position = container.style.position || 'relative';
  container.style.overflow = 'hidden';
  container.style.overscrollBehavior = 'contain';
  container.style.touchAction = 'none';

  // ===== 1) 엔진/러너 =====
  const engine = Engine.create();
  // ✅ 올바른 경로: engine.gravity.y
  engine.gravity.y = 0.3;
  engine.enableSleeping = true;
  const runner = Runner.create();

  // 디버그 렌더
  let render = null;
  if (debug) {
    render = Render.create({
      element: container,
      engine,
      options: {
        width: container.clientWidth,
        height: container.clientHeight,
        wireframes: false,
        background: 'rgba(255,255,0,0.2)',
        pixelRatio: window.devicePixelRatio || 1,
      },
    });
    Render.run(render);
  }

  // ===== 2) 레이어 =====
  let badgeLayer = container.querySelector('.badge-layer');
  if (!badgeLayer) {
    badgeLayer = document.createElement('div');
    badgeLayer.className = 'badge-layer';
    container.appendChild(badgeLayer);
  }

  // ===== 3) 벽 =====
  const wallThickness = 200;
  const makeWalls = () => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    const dummyMargin = debug ? 20 : 0;
    return [
      Bodies.rectangle(
        w / 2,
        -wallThickness / 2 + dummyMargin,
        w,
        wallThickness,
        {
          isStatic: true,
          label: 'wall-top',
          render: { fillStyle: 'rgba(255,0,0,.5)' },
        }
      ),
      Bodies.rectangle(
        w / 2,
        h + wallThickness / 2 - dummyMargin,
        w,
        wallThickness,
        {
          isStatic: true,
          label: 'wall-bottom',
          render: { fillStyle: 'rgba(0,200,0,.5)' },
        }
      ),
      Bodies.rectangle(
        -wallThickness / 2 + dummyMargin,
        h / 2,
        wallThickness,
        h,
        {
          isStatic: true,
          label: 'wall-left',
          render: { fillStyle: 'rgba(0,128,255,.5)' },
        }
      ),
      Bodies.rectangle(
        w + wallThickness / 2 - dummyMargin,
        h / 2,
        wallThickness,
        h,
        {
          isStatic: true,
          label: 'wall-right',
          render: { fillStyle: 'rgba(255,0,200,.5)' },
        }
      ),
    ];
  };
  let walls = Composite.create({ label: 'walls' });
  Composite.add(walls, makeWalls());
  World.add(engine.world, walls);

  // ===== 4) 상태 =====
  const byId = Object.create(null);
  const items = [];

  // ===== 5) 배지 생성 =====
  const createBadge = ({ id, label, color }, idx) => {
    // DOM
    const el = document.createElement('div');
    el.className = 'badge';
    el.dataset.id = id;
    el.textContent = label;
    if (color) el.style.background = color;
    badgeLayer.appendChild(el);

    // 측정
    const prev = el.style.transform;
    el.style.visibility = 'hidden';
    el.style.transform = 'translate(-50%, -50%) translate(0px, -10000px)';
    const rect = el.getBoundingClientRect();
    const width = Math.max(48, rect.width || 120);
    const height = Math.max(32, rect.height || 36);
    el.style.transform = prev;
    el.style.visibility = 'visible';

    // Body
    const w = container.clientWidth;
    const x = w * 0.2 + (idx % 5) * (w * 0.12);
    const y = 40 + (idx % 4) * 40;

    const body = Bodies.rectangle(x, y, width, height, {
      restitution: 0.8,
      friction: 0.1,
      frictionAir: 0.02,
      chamfer: { radius: height / 2 },
      label: 'badge',
      plugin: { data: { id, label, color } },
      render: debug
        ? { fillStyle: color || '#ccc', strokeStyle: '#0f0', lineWidth: 2 }
        : {
            fillStyle: 'transparent',
            strokeStyle: 'transparent',
            lineWidth: 0,
          },
    });

    World.add(engine.world, body);

    const pos = body.position;
    el.style.transform = `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) rotate(0rad)`;

    const record = { id, el, body, width, height, label, color };
    byId[id] = record;
    items.push(record);
  };

  // ===== 6) 동기화(매 틱) =====
  const syncBadges = () => {
    for (const b of items) {
      const { x, y } = b.body.position;
      const angle = b.body.angle;
      b.el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${angle}rad)`;
    }
  };
  Events.on(engine, 'afterUpdate', syncBadges);

  // ===== 7) 사이즈 재측정 & 스케일 =====
  const resyncBadgeSizes = (targets = items) => {
    for (const b of targets) {
      const { el, body } = b;

      const prev = el.style.transform;
      const { x, y } = body.position;
      el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;

      const rect = el.getBoundingClientRect();
      const targetW = Math.max(48, rect.width || 1);
      const targetH = Math.max(32, rect.height || 1);

      const sx = targetW / b.width;
      const sy = targetH / b.height;

      const near = (v) => Math.abs(v - 1) < 0.02;
      if (!near(sx) || !near(sy)) {
        Body.scale(body, sx, sy);
        b.width = targetW;
        b.height = targetH;
        Body.setVelocity(body, {
          x: body.velocity.x * 0.6,
          y: body.velocity.y * 0.6,
        });
        Body.setAngularVelocity(body, body.angularVelocity * 0.6);
      }

      el.style.transform = prev;
    }
  };

  // ===== 8) 라벨 업데이트 =====
  const updateLabel = (id, newLabel) => {
    const b = byId[id];
    if (!b) return false;
    b.el.textContent = newLabel;
    b.body.plugin ??= {};
    b.body.plugin.data ??= {};
    b.body.plugin.data.label = newLabel;
    b.label = newLabel;
    resyncBadgeSizes([b]);
    return true;
  };

  // ===== 9) 스냅 & 세틀 =====
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const snapBadgeIntoView = (b, container, padding = 6) => {
    const { body } = b;
    const { min, max } = body.bounds; // 회전 고려 AABB
    const w = container.clientWidth;
    const h = container.clientHeight;

    const bw = max.x - min.x;
    const bh = max.y - min.y;
    let { x, y } = body.position;

    const minX = padding + bw / 2;
    const maxX = w - padding - bw / 2;
    const minY = padding + bh / 2;
    const maxY = h - padding - bh / 2;

    const safeX = minX <= maxX ? clamp(x, minX, maxX) : w / 2;
    const safeY = minY <= maxY ? clamp(y, minY, maxY) : h / 2;

    if (safeX !== x || safeY !== y) {
      const jitter = 0.5;
      Body.setPosition(body, {
        x: safeX + (Math.random() - 0.5) * jitter,
        y: safeY + (Math.random() - 0.5) * jitter,
      });
      Body.setVelocity(body, {
        x: body.velocity.x * 0.4,
        y: body.velocity.y * 0.4,
      });
      Body.setAngularVelocity(body, body.angularVelocity * 0.4);
    }
  };
  const snapAllBadgesIntoView = (badges, container) => {
    for (const b of badges) snapBadgeIntoView(b, container, 6);
  };

  const settleAfterResize = (
    badges,
    { nudgeVelocity = { x: 0, y: 0.6 }, nudgeForce = { x: 0, y: 1 } } = {}
  ) => {
    for (const b of badges) {
      const body = b.body;
      if (body.isSleeping) Sleeping.set(body, false);
      Body.setVelocity(body, {
        x: body.velocity.x * 0.5 + nudgeVelocity.x,
        y: body.velocity.y * 0.5 + nudgeVelocity.y,
      });
      Body.applyForce(body, body.position, nudgeForce);
    }
  };

  // ===== 10) 리사이즈 핸들러 =====
  let resizeTimer = null;
  const handleResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      Composite.clear(walls, true, true);
      World.remove(engine.world, walls);
      walls = Composite.create({ label: 'walls' });
      Composite.add(walls, makeWalls());
      World.add(engine.world, walls);

      // 디버그 렌더 캔버스 크기 동기화
      if (render) {
        render.canvas.width =
          container.clientWidth * (window.devicePixelRatio || 1);
        render.canvas.height =
          container.clientHeight * (window.devicePixelRatio || 1);
        render.options.width = container.clientWidth;
        render.options.height = container.clientHeight;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const run = () => {
            resyncBadgeSizes();
            snapAllBadgesIntoView(items, container);
            settleAfterResize(items);
          };
          if (document.fonts?.ready) document.fonts.ready.then(run);
          else run();
        });
      });
    }, 300);
  };

  let ro = null;
  if ('ResizeObserver' in window) {
    ro = new ResizeObserver(handleResize);
    ro.observe(container);
  } else {
    window.addEventListener('resize', handleResize, { passive: true });
  }

  // ===== 11) 드래그 =====
  const mouse = Mouse.create(container);
  const mouseConstraint = MouseConstraint.create(engine, {
    mouse,
    constraint: { stiffness: 0.2, render: { visible: false } },
  });
  World.add(engine.world, mouseConstraint);

  // 휠 이벤트 제거(트랙패드 스크롤 영향 최소화)
  mouse.element.removeEventListener('mousewheel', mouse.mousewheel);
  mouse.element.removeEventListener('DOMMouseScroll', mouse.mousewheel);
  mouse.element.removeEventListener('wheel', mouse.mousewheel);

  // ===== 12) 보일 때 시작(IntersectionObserver) =====
  const defaultGravity = 0.3;
  let io = null;

  const setAllSleeping = (sleep) => {
    for (const b of items) Sleeping.set(b.body, sleep);
  };
  const nudgeAll = () => {
    for (const b of items) {
      const body = b.body;
      Sleeping.set(body, false);
      Body.setVelocity(body, {
        x: body.velocity.x * 0.5,
        y: body.velocity.y * 0.5 + 0.6,
      });
      Body.applyForce(body, body.position, { x: 0, y: 0.0005 });
    }
  };

  const play = () => {
    engine.gravity.y = defaultGravity;
    Runner.run(runner, engine);
    nudgeAll();
    requestAnimationFrame(() => container.classList.add('is-play'));
  };

  const pause = () => {
    requestAnimationFrame(() => container.classList.remove('is-play'));
    engine.gravity.y = 0;
    setAllSleeping(true);
    // 성능 아끼려면 Runner.stop도 가능하지만,
    // afterUpdate 동기화를 계속 쓰려면 유지해도 된다.
  };

  if (startOnVisible) {
    // 초기는 멈춤 상태
    engine.gravity.y = 0;
    setAllSleeping(true);

    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.target !== container) continue;
            if (e.isIntersecting && e.intersectionRatio >= visibleThreshold) {
              play();
              if (visibleOnce) {
                io.disconnect();
                io = null;
              }
            } else if (!visibleOnce) {
              pause();
            }
          }
        },
        { root: null, threshold: [visibleThreshold] }
      );
      io.observe(container);
    } else {
      // 폴백: 간단 스크롤 체크
      const isVisible = () => {
        const r = container.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const vw = window.innerWidth || document.documentElement.clientWidth;
        const visibleX = Math.max(
          0,
          Math.min(r.right, vw) - Math.max(r.left, 0)
        );
        const visibleY = Math.max(
          0,
          Math.min(r.bottom, vh) - Math.max(r.top, 0)
        );
        const area = r.width * r.height;
        const visibleArea = visibleX * visibleY;
        return area > 0 && visibleArea / area >= visibleThreshold;
      };
      const onScroll = () => {
        if (isVisible()) {
          play();
          if (visibleOnce) window.removeEventListener('scroll', onScroll, true);
        } else if (!visibleOnce) {
          pause();
        }
      };
      window.addEventListener('scroll', onScroll, true);
      requestAnimationFrame(onScroll);
    }
  } else {
    // 즉시 시작
    Runner.run(runner, engine);
  }

  // ===== 13) 외부 API & destroy =====
  const add = (labelOrObj) => {
    const [obj] = normalizeLabels([labelOrObj]);
    createBadge(obj, items.length);
    resyncBadgeSizes([byId[obj.id]]);
    return obj.id;
  };

  const getLabel = (id) => byId[id]?.label;
  const getData = (id) => byId[id]?.body?.plugin?.data;

  const destroy = () => {
    if (io) io.disconnect();
    if (ro && typeof ro.disconnect === 'function') ro.disconnect();
    else window.removeEventListener('resize', handleResize);
    Runner.stop(runner);
    World.clear(engine.world, false);
    Engine.clear(engine);
    if (render) {
      Render.stop(render);
      if (render.canvas?.parentNode)
        render.canvas.parentNode.removeChild(render.canvas);
    }
    container.querySelectorAll('.badge').forEach((n) => n.remove());
  };

  // ==== 기타
  const sampleWithoutReplacement = (arr, k) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a.slice(0, Math.min(k, a.length));
  };

  const initialN =
    typeof initialCount === 'number' && initialCount >= 0
      ? Math.min(initialCount, labels.length)
      : labels.length;

  const initialList = randomInit
    ? sampleWithoutReplacement(labels, initialN)
    : labels.slice(0, initialN);

  initialList.forEach((obj, i) => createBadge(obj, i));

  const getUnusedLabels = () => labels.filter((o) => !byId[o.id]);

  /** 남아있는 라벨들 중 랜덤으로 n개 생성 */
  const addRandom = (n = 1) => {
    const unused = getUnusedLabels();
    if (!unused.length) return [];
    const picks = sampleWithoutReplacement(unused, n);
    picks.forEach((obj) => createBadge(obj, items.length));
    // 생성 직후 사이즈/위치 정합
    resyncBadgeSizes(picks.map((p) => byId[p.id]));
    return picks.map((p) => p.id);
  };

  return {
    add,
    updateLabel,
    getLabel,
    getData,
    resyncBadgeSizes,
    play,
    pause,
    destroy,
    engine,
    addRandom,
  };
};

export default InitBadge;
